import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TopNav from "../home/components/TopNav";

type DataItem = {
  id: string;
  name: string;
  type: string;
  size: string;
  records: number;
  status: "ready" | "processing" | "error";
  updatedAt: string;
  source: string;
};

const mockDatasets: DataItem[] = [
  { id: "ds001", name: "朝阳区小区楼栋完整导出_2024Q4", type: "Excel", size: "4.2 MB", records: 3240, status: "ready", updatedAt: "2024-12-18", source: "地图导出" },
  { id: "ds002", name: "望京区域工业园区报告", type: "CSV", size: "1.8 MB", records: 126, status: "ready", updatedAt: "2024-12-15", source: "地图导出" },
  { id: "ds003", name: "商务楼宇企业数据_批量导入", type: "JSON", size: "8.6 MB", records: 9870, status: "processing", updatedAt: "2024-12-20", source: "API导入" },
  { id: "ds004", name: "北京市沿街商铺汇总", type: "Excel", size: "2.3 MB", records: 5412, status: "ready", updatedAt: "2024-12-10", source: "手动上传" },
  { id: "ds005", name: "选址评分分析结果_上海静安", type: "CSV", size: "0.9 MB", records: 88, status: "ready", updatedAt: "2024-12-08", source: "API导出" },
  { id: "ds006", name: "竞品分布专题数据_导入中", type: "Excel", size: "—", records: 0, status: "error", updatedAt: "2024-12-19", source: "手动上传" },
];

const storageUsed = 17.8;
const storageTotal = 50;

export default function PersonalSpacePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"manage" | "import" | "download">("manage");
  const [selected, setSelected] = useState<string[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const toggleSelect = (id: string) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    setUploadedFiles((prev) => [...prev, ...files.map((f) => f.name)]);
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'Noto Sans SC', sans-serif" }}>
      <TopNav />

      {/* Page header */}
      <div className="pt-20 pb-0">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex items-end justify-between py-6 border-b border-slate-200">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">个人数据空间</h1>
              <p className="text-slate-500 text-sm mt-1">管理您的数据资产，支持导入、整理与下载</p>
            </div>
            {/* Storage bar */}
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-xs text-slate-500 mb-1">已使用存储</div>
                <div className="text-sm font-bold text-slate-800">{storageUsed} GB <span className="text-slate-400 font-normal">/ {storageTotal} GB</span></div>
              </div>
              <div className="w-40 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full transition-all"
                  style={{ width: `${(storageUsed / storageTotal) * 100}%` }}
                />
              </div>
              <button className="bg-[#1e3d7a] hover:bg-[#2550a0] text-white text-sm font-medium px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap transition-colors">
                扩容升级
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mt-4">
            {(["manage", "import", "download"] as const).map((tab) => {
              const labels = { manage: "数据管理", import: "数据导入", download: "数据下载" };
              const icons = { manage: "ri-database-2-line", import: "ri-upload-cloud-2-line", download: "ri-download-cloud-2-line" };
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === tab ? "bg-white border border-slate-200 text-[#1e3d7a] font-semibold" : "text-slate-500 hover:text-slate-700"}`}
                >
                  <i className={icons[tab]}></i>
                  {labels[tab]}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-8 py-6">
        {/* ---- Data Management ---- */}
        {activeTab === "manage" && (
          <div>
            {/* Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              {[
                { label: "数据集总数", val: "6", icon: "ri-folder-3-line", color: "text-blue-600", bg: "bg-blue-50" },
                { label: "数据总条数", val: "18,736", icon: "ri-bar-chart-grouped-line", color: "text-emerald-600", bg: "bg-emerald-50" },
                { label: "本月新增", val: "3", icon: "ri-add-circle-line", color: "text-amber-600", bg: "bg-amber-50" },
                { label: "待处理任务", val: "2", icon: "ri-time-line", color: "text-rose-600", bg: "bg-rose-50" },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-xl border border-slate-200 px-5 py-4 flex items-center gap-4">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${s.bg}`}>
                    <i className={`${s.icon} ${s.color} text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">{s.label}</div>
                    <div className="text-xl font-bold text-slate-800 mt-0.5">{s.val}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Toolbar */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                  <input
                    type="text"
                    placeholder="搜索数据集名称..."
                    className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-[#1e3d7a] w-60"
                  />
                </div>
                <select className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-[#1e3d7a] text-slate-600 cursor-pointer">
                  <option>全部类型</option>
                  <option>Excel</option>
                  <option>CSV</option>
                  <option>JSON</option>
                </select>
              </div>
              {selected.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">已选 {selected.length} 项</span>
                  <button className="text-xs bg-rose-50 border border-rose-200 text-rose-600 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-rose-100 whitespace-nowrap">
                    <i className="ri-delete-bin-line mr-1"></i>删除
                  </button>
                  <button className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-600 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-emerald-100 whitespace-nowrap">
                    <i className="ri-download-2-line mr-1"></i>批量下载
                  </button>
                </div>
              )}
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="w-10 px-4 py-3">
                      <input type="checkbox" className="cursor-pointer" onChange={(e) => setSelected(e.target.checked ? mockDatasets.map((d) => d.id) : [])} />
                    </th>
                    {["数据集名称", "类型", "记录数", "大小", "来源", "更新时间", "状态", "操作"].map((h) => (
                      <th key={h} className="text-left text-xs font-semibold text-slate-500 px-4 py-3 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mockDatasets.map((item) => (
                    <tr key={item.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3.5">
                        <input type="checkbox" checked={selected.includes(item.id)} onChange={() => toggleSelect(item.id)} className="cursor-pointer" />
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-7 h-7 flex items-center justify-center rounded-md flex-shrink-0 ${item.type === "Excel" ? "bg-emerald-100 text-emerald-600" : item.type === "CSV" ? "bg-amber-100 text-amber-600" : "bg-blue-100 text-blue-600"}`}>
                            <i className={`${item.type === "JSON" ? "ri-braces-line" : "ri-file-excel-2-line"} text-sm`}></i>
                          </div>
                          <span className="text-sm text-slate-800 font-medium">{item.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-xs text-slate-500">{item.type}</td>
                      <td className="px-4 py-3.5 text-xs text-slate-700 font-mono">{item.records.toLocaleString()}</td>
                      <td className="px-4 py-3.5 text-xs text-slate-700 font-mono">{item.size}</td>
                      <td className="px-4 py-3.5 text-xs text-slate-500">{item.source}</td>
                      <td className="px-4 py-3.5 text-xs text-slate-500">{item.updatedAt}</td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${item.status === "ready" ? "bg-emerald-50 text-emerald-600" : item.status === "processing" ? "bg-amber-50 text-amber-600" : "bg-rose-50 text-rose-600"}`}>
                          <span className={`w-1.5 h-1.5 rounded-full inline-block ${item.status === "ready" ? "bg-emerald-400" : item.status === "processing" ? "bg-amber-400 animate-pulse" : "bg-rose-400"}`}></span>
                          {item.status === "ready" ? "就绪" : item.status === "processing" ? "处理中" : "导入失败"}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1">
                          <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500 hover:text-[#1e3d7a] transition-colors cursor-pointer" title="下载">
                            <i className="ri-download-2-line text-sm"></i>
                          </button>
                          <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors cursor-pointer" title="预览">
                            <i className="ri-eye-line text-sm"></i>
                          </button>
                          <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-500 transition-colors cursor-pointer" title="删除">
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ---- Data Import ---- */}
        {activeTab === "import" && (
          <div className="max-w-3xl">
            <h2 className="text-lg font-bold text-slate-800 mb-1">导入数据</h2>
            <p className="text-slate-500 text-sm mb-6">支持 Excel、CSV、JSON 格式，单次最大上传 100MB</p>

            {/* Drop zone */}
            <div
              className={`border-2 border-dashed rounded-2xl p-12 text-center transition-colors cursor-pointer ${dragOver ? "border-[#1e3d7a] bg-blue-50/40" : "border-slate-300 bg-white hover:border-slate-400"}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" multiple accept=".xlsx,.csv,.json" className="hidden" onChange={(e) => { const files = Array.from(e.target.files || []); setUploadedFiles((prev) => [...prev, ...files.map((f) => f.name)]); }} />
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-slate-100 mx-auto mb-4">
                <i className="ri-upload-cloud-2-line text-3xl text-slate-400"></i>
              </div>
              <div className="text-slate-700 font-semibold mb-1">拖拽文件到此处，或点击选择文件</div>
              <div className="text-slate-400 text-sm">支持 .xlsx  .csv  .json，最大 100MB</div>
              {uploadedFiles.length > 0 && (
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {uploadedFiles.map((f, i) => (
                    <span key={i} className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1 rounded-full">{f}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Format instructions */}
            <div className="mt-6 grid grid-cols-3 gap-4">
              {[
                { fmt: "Excel (.xlsx)", icon: "ri-file-excel-2-line", color: "text-emerald-600", tips: ["第一行为表头", "字段名与系统字段对应", "支持多 Sheet 页"] },
                { fmt: "CSV (.csv)", icon: "ri-file-text-line", color: "text-amber-600", tips: ["UTF-8 编码", "逗号分隔", "第一行为表头"] },
                { fmt: "JSON (.json)", icon: "ri-braces-line", color: "text-blue-600", tips: ["数组格式", "每项为一条记录", "字段名保持一致"] },
              ].map((f) => (
                <div key={f.fmt} className="bg-white border border-slate-200 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <i className={`${f.icon} ${f.color} text-lg`}></i>
                    <span className="text-sm font-semibold text-slate-700">{f.fmt}</span>
                  </div>
                  <ul className="space-y-1.5">
                    {f.tips.map((t) => (
                      <li key={t} className="flex items-center gap-1.5 text-xs text-slate-500">
                        <i className="ri-check-line text-slate-400"></i>{t}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {uploadedFiles.length > 0 && (
              <button className="mt-6 w-full bg-[#1e3d7a] hover:bg-[#2550a0] text-white font-semibold py-3 rounded-xl transition-colors cursor-pointer">
                开始导入 ({uploadedFiles.length} 个文件)
              </button>
            )}
          </div>
        )}

        {/* ---- Download ---- */}
        {activeTab === "download" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-slate-800">数据下载</h2>
                <p className="text-slate-500 text-sm mt-0.5">从地图/API 导出的文件，可在此统一下载管理</p>
              </div>
              <button className="flex items-center gap-2 text-sm border border-slate-200 bg-white text-slate-600 hover:text-slate-800 px-4 py-2 rounded-lg cursor-pointer transition-colors whitespace-nowrap">
                <i className="ri-refresh-line"></i>刷新列表
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {mockDatasets.filter((d) => d.status === "ready").map((item) => (
                <div key={item.id} className="bg-white border border-slate-200 rounded-xl px-6 py-4 flex items-center gap-5">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${item.type === "Excel" ? "bg-emerald-100 text-emerald-600" : item.type === "CSV" ? "bg-amber-100 text-amber-600" : "bg-blue-100 text-blue-600"}`}>
                    <i className={`${item.type === "JSON" ? "ri-braces-line" : "ri-file-excel-2-line"} text-lg`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">{item.name}</div>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-xs text-slate-400">{item.type} · {item.size} · {item.records.toLocaleString()} 条记录</span>
                      <span className="text-xs text-slate-400">{item.updatedAt}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button className="flex items-center gap-1.5 bg-[#1e3d7a] hover:bg-[#2550a0] text-white text-xs px-4 py-2 rounded-lg cursor-pointer transition-colors whitespace-nowrap">
                      <i className="ri-download-2-line"></i>下载
                    </button>
                    <button className="flex items-center gap-1.5 border border-slate-200 text-slate-600 hover:text-slate-800 text-xs px-3 py-2 rounded-lg cursor-pointer transition-colors whitespace-nowrap">
                      <i className="ri-eye-line"></i>预览
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Batch download */}
            <div className="mt-6 bg-slate-50 border border-dashed border-slate-300 rounded-xl p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-slate-200">
                  <i className="ri-archive-line text-slate-600 text-lg"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-800">批量下载全部就绪文件</div>
                  <div className="text-xs text-slate-500 mt-0.5">共 4 个文件，约 9.2 MB，打包为 ZIP 格式下载</div>
                </div>
              </div>
              <button className="flex items-center gap-2 bg-white border border-slate-300 hover:border-[#1e3d7a] text-slate-700 hover:text-[#1e3d7a] text-sm px-5 py-2.5 rounded-lg cursor-pointer transition-colors whitespace-nowrap">
                <i className="ri-download-cloud-2-line"></i>一键打包下载
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
